import { Component, ElementRef, HostListener, Inject, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { IServiceOpcode, OpcodeType, PricingType } from '@signal/asp-data-commons';
import { Observable } from 'rxjs';
import { OpCodeState } from 'src/app/modules/store/opcode/opcode.reducer';
import * as fromOpCodeSelectors from './../../../store/opcode/opcode.selectors';
import * as fromOpCodeActions from './../../../store/opcode/opcode.actions';
import { debounceTime } from 'rxjs/operators';
import { SubscriptionList } from '../../../shared/models/asp.types';
import { unsubscribeSubscriptions } from '../../../shared/services/util.service';

@Component({
  selector: 'app-create-opcode-form',
  templateUrl: './create-opcode-form.component.html',
  styleUrls: ['./create-opcode-form.component.scss']
})
export class CreateOpcodeFormComponent implements OnInit {
  mileages$: Observable<number[]>;
  opcodeForm: UntypedFormGroup;
  readonly pricingType = PricingType;
  isCheckOpcodesLoading = false;
  isOpcodeExists$: Observable<boolean>;
  isOpcodeExists = false;
  isEnglishText:boolean = true;
  clickedOutside=false;
  @ViewChild('form') form: ElementRef;
  private subs: SubscriptionList = {};
  allOpCodes: IServiceOpcode[];
  filteredOptions: IServiceOpcode[];
  allDmsOpcodes: IServiceOpcode[];
  
  constructor(public readonly dialogRef: MatDialogRef<CreateOpcodeFormComponent>,
    public readonly formBuilder: UntypedFormBuilder,
    private readonly opCodeStore: Store<OpCodeState>,
    private readonly translate: TranslateService,
    @Inject(MAT_DIALOG_DATA) public readonly data: any) { 
      if(this.translate.currentLang?.includes('es')){
        this.isEnglishText = false;
      }
      }

  ngOnInit(): void {
    this.InitializeForm();
    this.mileages$ = this.opCodeStore.select(fromOpCodeSelectors.selectAllMileages);
    this.opCodeStore.select(fromOpCodeSelectors.selectOpCodes).subscribe(t=> {
      this.allOpCodes = t || [];
    });
    this.subs.dmsOpcodes = this.opCodeStore.select(fromOpCodeSelectors.selectDmsOpCodes).subscribe(opcodes=> {
      this.filteredOptions = opcodes?.slice();
      this.allDmsOpcodes = opcodes?.slice();
    });
  }

  @HostListener("document:click", ['$event.target'])
  showMessage(event)
  {    
    if (!this.form.nativeElement.contains(event))
    {
      if(this.data.clicked) {
        this.data.clicked=false;
      }
      else{
        this.clickedOutside=true;
      }
    }
  }

  checkExistingOpcode(opcode) {
    return this.allOpCodes.findIndex(x=> x.opcode?.toLowerCase() === opcode.toLowerCase()) !== -1;
  }
  
  InitializeForm() {
    this.opcodeForm = this.formBuilder.group({
      labourHours: [null, [Validators.required, Validators.min(0.1)]],
      commentsEnabled: [false],
      commentsRequired: [false],
      isMiscellaneous: [false],
      isTXM: [false],
      opcodeType: ['', Validators.required],
      dealerCode: [''],
      displayName: ['', [Validators.required]],
      cause: [''],
      dmsDescription: [''],
      description: ['', [Validators.required]],
      isNew: [''],
      isVisible: [false],
      opcode: ['',[Validators.required]],
      recommended: this.formBuilder.group({
        isRecommended: [false],
        isCoveredByToyotaCare: [false],
        isTelematics: [false],
        isSSCRecall: [false]
      }),
      addOnOpcodes: this.formBuilder.group({
        checked: [false],
        description: [''],
        opcodes: this.formBuilder.array([])
      }),
      tags: [''],
      correction: [''],
      pricing: this.formBuilder.group({
        startsAt: [''],
        range: this.formBuilder.group({
          from: [''],
          to: ['']
        }),
        fixed: [''],
        dms: [''],
        show: [false],
        pricingType: [this.pricingType.FIXED],
        discount: this.formBuilder.group({
          isAvailable: [false],
          validTill: [''],
          price: ['']
        })
      }),
      mileages: [''],
      isCompRentalEligible: [false],
      mapDmsCode: this.formBuilder.group({
        opcode: [''],
        opcodeOption: [''],
        isDmsCodeSaved: [false]
      }),
      isAspCreated: [true],
      isInternalCode: [true],
      subComplaints: this.formBuilder.group({
        checked: [false],
        complaintCodes: this.formBuilder.array([])
      }),
    });
    this.detectFormValueChanges();
  }
  detectFormValueChanges(){
    this.subs.formChanges = this.opcodeForm.get('opcode').valueChanges.pipe(debounceTime(400)).subscribe((t: string) => {
      if (t.trim().length > 0)
      {
        this.isCheckOpcodesLoading = true;
        this.isOpcodeExists = this.checkExistingOpcode(t);
        this.isCheckOpcodesLoading = false;
      }
      if(t.trim().length == 0){
        this.isOpcodeExists = false;
      }
    });
  }
  clearForm(){
    this.opCodeStore.dispatch(new fromOpCodeActions.ChangeIsExistingOpcode());
    this.InitializeForm();
  }
  get commentsEnabledField() {
    return this.opcodeForm.get('commentsEnabled');
  }

  get commentsRequiredField() {
    return this.opcodeForm.get('commentsRequired');
  }

  get isMisc() {
    return this.opcodeForm.get('isMiscellaneous');
  }


  get opcodeTypeField() {
    return this.opcodeForm.get('opcodeType');
  }
  get pricingTypeField() {
    return this.opcodeForm.get('pricingType');
  }

  get opcodeDescription() {
    return this.opcodeForm.get('description');
  }

  get dmsCode() {
    return this.opcodeForm.get('mapDmsCode') as UntypedFormGroup;
  }

  hasTouched(controlName: string){
    return this.opcodeForm.get(controlName).touched;
  }
  hasError(controlName: string, errorName: string) {
    return this.opcodeForm.get(controlName).hasError(errorName);
  }
  close() {
    this.dialogRef.close();
    this.opCodeStore.dispatch(new fromOpCodeActions.ChangeIsExistingOpcode());
  }
  updateSwitchValues(val: boolean) {
    this.opcodeForm.get('isVisible').patchValue(val);
  }

  submitForm() {
      if (this.isValidForm() && !(this.isCheckOpcodesLoading) && !(this.isOpcodeExists) ) {

        if(!this.opcodeForm.value?.pricing?.fixed){
          delete this.opcodeForm.value.pricing.fixed;
        }
  
        if(!this.opcodeForm.value?.pricing?.dms){
          delete this.opcodeForm.value.pricing.dms;
        }

        delete this.opcodeForm.value.mapDmsCode.isDmsCodeSaved;

        delete this.opcodeForm.value.mapDmsCode.opcodeOption;
  
        this.opcodeForm.value.labourHours = +this.opcodeForm.value.labourHours;
  
        const opcodeData = { ...this.opcodeForm.value, isNew: false };
  
        // set milage as array before submitting
        let mileage = this.opcodeForm.value.mileages;
        if(mileage !== ''){
          mileage = Array.isArray(mileage) ? mileage : [mileage];
        }
        opcodeData.mileages = mileage;
  
        this.opCodeStore.dispatch(new fromOpCodeActions.CreateOpCode({ opcode: opcodeData }));
        this.close();
      }
    }

    addMileageValidation(){
      if(this.opcodeForm.value.mileages.length == 0){
        this.opcodeForm.get('mileages').setErrors({ required: this.translate.instant('opcodeServiceManagement.mileageIsRequiredWhenMaintenanceOpcodeIsChoosen') });
      }
    }
  
    removeMileageValidation(){
      this.opcodeForm.get('mileages').setErrors(null, { emitEvent: false });
    }

    filter(value): void {
      const filterValue = value.toLowerCase();
      this.filteredOptions = this.allDmsOpcodes.filter(o => o.opcode.toLowerCase().includes(filterValue)
       || (o.displayName ? o.displayName : o.dmsDescription).toLowerCase().includes(filterValue));
    }
  
    mapDmsOpcode() {
      if(this.dmsCode.value.opcodeOption) {
        this.dmsCode.controls['opcode'].patchValue(this.dmsCode.value.opcodeOption);
        this.dmsCode.controls['isDmsCodeSaved'].patchValue(true);
      }
    }
  
    onEditDmsOpcode() {
      this.dmsCode.controls['isDmsCodeSaved'].patchValue(false);
    }
  
    onDeleteDmsOpcode() {
      this.dmsCode.controls['opcode'].patchValue('');
      this.dmsCode.controls['opcodeOption'].patchValue('');
      this.dmsCode.controls['isDmsCodeSaved'].patchValue(false);
      this.filteredOptions = this.allDmsOpcodes.slice();
    }

    isValidForm() {
      return this.opcodeForm.valid && ( this.dmsCode.value.opcode && this.dmsCode.value.isDmsCodeSaved );
    }

    ngOnChanges() {
      unsubscribeSubscriptions(this.subs);
    }

}
